import {
  GET_EXCHANGES,
  GET_MARKET_INFO,
  GET_SETTINGS,
  GET_USER_INFO,
  FEEDBACK_STATE,
} from "@/store/modules/user/consts";
import {
  loadCurrenciesForCart,
  needsCurrenciesFetch,
} from "@/utils/currenciesLoader";
import {
  shouldFetchExchangesList,
  shouldFetchFeedback,
  shouldFetchMarketInfo,
  shouldFetchSettings,
  shouldFetchUserInfo,
} from "@/utils/sessionDataCache";

export {
  hasMarketInfoForMerchant,
  hasCachedUserInfo,
  hasCachedSettings,
  hasCachedExchanges,
  shouldFetchCurrencies,
} from "@/utils/sessionDataCache";

export {
  hasLoadedCurrenciesForCart,
  needsCurrenciesFetch,
  loadCurrenciesForCart,
} from "@/utils/currenciesLoader";

/** @deprecated Use loadCurrenciesForCart */
export { loadCurrenciesForCart as fetchPaymentCurrenciesOnce } from "@/utils/currenciesLoader";

function canBootstrapPayment(store) {
  return Boolean(store.getters?.["auth/isAuth"]);
}

/** Merchant name/logo for Sign-in, Welcome, and Header — no auth required. */
export function loadMerchantMarketInfo(store, cart) {
  if (!cart?.merchant_id) {
    return Promise.resolve();
  }
  const merchantId = cart.merchant_id;
  if (!shouldFetchMarketInfo(store.state.user, merchantId)) {
    return Promise.resolve(store.state.user.marketInfo);
  }
  return store.dispatch(`user/${GET_MARKET_INFO}`, { merchantId });
}

function pushDispatches(store, cart, checks) {
  if (!cart?.merchant_id) {
    return [];
  }

  const merchantId = cart.merchant_id;
  const state = store.state.user;
  const dispatches = [];

  checks.forEach(({ shouldFetch, build }) => {
    if (shouldFetch(state, merchantId, cart)) {
      dispatches.push(build(store, merchantId, cart));
    }
  });

  return dispatches;
}

/** Market info + payment settings (required for tabs / display currency). */
export function buildCriticalPaymentDispatches(store, cart) {
  return pushDispatches(store, cart, [
    {
      shouldFetch: (state, merchantId) =>
        shouldFetchMarketInfo(state, merchantId),
      build: (store, merchantId) =>
        store.dispatch(`user/${GET_MARKET_INFO}`, { merchantId }),
    },
    {
      shouldFetch: (state) => shouldFetchSettings(state),
      build: (store) => store.dispatch(`user/${GET_SETTINGS}`),
    },
  ]);
}

/** Exchanges catalog, user profile, feedback — non-blocking. */
export function buildBackgroundPaymentDispatches(store, cart) {
  return pushDispatches(store, cart, [
    {
      shouldFetch: (state) => shouldFetchExchangesList(state),
      build: (store) => store.dispatch(`user/${GET_EXCHANGES}`),
    },
    {
      shouldFetch: (state) => shouldFetchUserInfo(state),
      build: (store) => store.dispatch(`user/${GET_USER_INFO}`),
    },
    {
      shouldFetch: (state) => shouldFetchFeedback(state),
      build: (store) => store.dispatch(`user/${FEEDBACK_STATE}`),
    },
  ]);
}

export function runBackgroundPaymentBootstrap(store, cart) {
  if (!canBootstrapPayment(store)) {
    return Promise.resolve();
  }
  const dispatches = buildBackgroundPaymentDispatches(store, cart);
  if (!dispatches.length) {
    return Promise.resolve();
  }
  return Promise.all(dispatches).catch((error) => {
    console.warn("[paymentBootstrap] background load failed:", error);
  });
}

/**
 * Warm-up after auth (Welcome Continue, Header on cart, iframe handoff).
 */
export function prefetchPaymentBootstrap(store, cart) {
  if (!cart?.merchant_id || !canBootstrapPayment(store)) {
    return Promise.resolve();
  }
  return Promise.all([
    loadCurrenciesForCart(store, cart),
    ...buildCriticalPaymentDispatches(store, cart),
    ...buildBackgroundPaymentDispatches(store, cart),
  ]).catch((error) => {
    console.warn("[paymentBootstrap] prefetch failed:", error);
  });
}

/**
 * Payment page critical path: currencies + market + settings (parallel).
 */
export function loadPaymentCriticalData(store, cart) {
  if (!cart?.merchant_id || !canBootstrapPayment(store)) {
    return Promise.resolve({ currencies: [] });
  }
  return Promise.all([
    loadCurrenciesForCart(store, cart),
    ...buildCriticalPaymentDispatches(store, cart),
  ]).then(([currencies]) => ({ currencies }));
}

export function paymentPageNeedsLoader(store, cart) {
  if (!canBootstrapPayment(store)) {
    return false;
  }
  const state = store.state.user;
  return (
    needsCurrenciesFetch(state, cart) ||
    buildCriticalPaymentDispatches(store, cart).length > 0
  );
}
